import { createContext, useContext, useEffect } from "react";
import { PathStore, StateError, getPath } from "shelving";
import { useInstance, useStore } from "shelving/react";
import { forward, redirect } from "shared/util/navigation";
import { MetadataContext } from "../context/MetadataContext.js";
import { type Routes, matchRoute } from "./Router.js";

/** Store the current navigator state. */
export class NavigatorStore extends PathStore {
	readonly routes: Routes;

	constructor(routes: Routes) {
		super(getPath(window.location.pathname));
		this.routes = routes;
	}

	forward(path: string): void {
		this.set(path);
		forward(this.value);
	}

	redirect(path: string): void {
		this.set(path);
		redirect(this.value);
	}
}

const CONTEXT = createContext<NavigatorStore | undefined>(undefined);

/** Use the current navigator in a component, if it exists. */
export function useOptionalNavigator() {
	return useContext(CONTEXT);
}

/** Use the current navigator in a component. */
export function useNavigator() {
	const router = useContext(CONTEXT);
	if (!router) throw new StateError("useNavigator() must be used inside <Navigator>");
	return router;
}

/**
 * Navigator is a router that uses the browser URL to select its active route.
 * - Tracks forward events by listening for `click` events on `<a href="">` elements in `document`
 * - Tracks back events using `popState` events on `window`
 */
export function Navigator({ routes, children = <NavigatorRoute /> }: { routes: Routes; children?: React.ReactNode }): React.ReactElement {
	const store = useStore(useInstance(NavigatorStore, routes));

	// Effect to attach listeners when this router is active.
	useEffect(() => {
		// Listen for `click` events on `<a href="">` anchors and fire the forward event instead.
		const onClick = (e: MouseEvent) => {
			// Look for clicks on `<a href="">` elements or `.targeted` elements containing `<a href="" class="target">`
			if (e.target instanceof Element) {
				const anchor = e.target.closest("a") || (!e.target.closest("button, label") && e.target.closest(".targeted")?.querySelector<HTMLAnchorElement>("a.target[href]"));
				if (anchor instanceof HTMLAnchorElement && anchor.host === window.location.host) {
					e.preventDefault();
					store.forward(anchor.pathname);
					return false; // Return false stops iOS web app opening every link in a new windows.
				}
			}
		};
		document.addEventListener("click", onClick);

		// Listen for `popstate` events and update the currently active path.
		const onPopState = () => store.set(window.location.pathname);
		window.addEventListener("popstate", onPopState);

		return () => {
			document.removeEventListener("click", onClick);
			window.removeEventListener("popstate", onPopState);
		};
	}, [store]);

	return <CONTEXT.Provider value={store}>{children}</CONTEXT.Provider>;
}

/**
 * Isolate a set of children so they remount on navigation.
 * * Uses `key=""` prop to remount entire child tree whenever the navigator value changes.
 * * This forces changes in the page to be completely remounted and not share e.g. data stores.
 */
export function NavigatorIsolation({ children }: { children: React.ReactNode }): React.ReactElement {
	const { value } = useStore(useNavigator());
	return (
		<MetadataContext key={value} path={value}>
			{children}
		</MetadataContext>
	);
}

/** Use the Navigator path to set the `path` property in `Metadata` */
export function NavigatorMetadata({ children }: { children: React.ReactNode }): React.ReactElement {
	const { value } = useStore(useNavigator());
	return <MetadataContext path={value}>{children}</MetadataContext>;
}

/** Show the content active for the current navigator route. */
export function NavigatorRoute(): React.ReactElement | null {
	const { value, routes } = useStore(useNavigator());
	return matchRoute(value, routes);
}
